import * as React from 'react';
import {
  Typography, Box, Button, Grid, Dialog, DialogTitle, DialogContent,
  DialogActions, FormControl, InputLabel, Select, MenuItem, Collapse, CircularProgress,
  Skeleton, useMediaQuery, useTheme,
} from '@mui/material';
import { connect } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { Table, Tag, Empty } from 'antd';
import { FilterList, FilterListOff, Search } from '@mui/icons-material';
import moment from 'moment';
import { useTranslation } from 'react-i18next';
import { centered_flex_box, sec_button, main_button, MainInput } from '../app/components/Styles';
import { getBazaar, getBazaarStock, getBazaarPurchases } from '../app/store/actions/dataActions';
import { cardShell, StatusPill, formatEgp, formatInt, pageTitle } from '../app/glass/styles';
import Hoverable from '../app/glass/Hoverable';

const PAYMENT_METHODS = ['Cash', 'Visa', 'Instapay'];

export const BazaarDetail = ({
  getBazaar, getBazaarStock, getBazaarPurchases,
  bazaar, shipments, performance, purchasesPreview,
  bazaarStock, bazaarPurchases, isLoading,
}) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [tab, setTab] = React.useState(0);
  const [purchaseModal, setPurchaseModal] = React.useState(null);
  const [showStockFilters, setShowStockFilters] = React.useState(false);
  const [showPurchaseFilters, setShowPurchaseFilters] = React.useState(false);
  const [stockSearch, setStockSearch] = React.useState('');
  const [stockPage, setStockPage] = React.useState(1);
  const [stockPageSize, setStockPageSize] = React.useState(10);
  const [purchaseSearch, setPurchaseSearch] = React.useState('');
  const [purchasePayment, setPurchasePayment] = React.useState('');
  const [purchasePage, setPurchasePage] = React.useState(1);
  const [purchasePageSize, setPurchasePageSize] = React.useState(10);

  const statusLabel = React.useCallback(
    (value) => t(`bazaars.statusLabels.${value}`, { defaultValue: value }),
    [t]
  );

  const paymentLabel = (method) => t(`bazaars.paymentMethods.${method}`, { defaultValue: method });

  const loadOverview = () => getBazaar(id);

  React.useEffect(() => { loadOverview(); }, [id]);

  React.useEffect(() => {
    if (tab === 1) {
      getBazaarStock(id, { page: stockPage, limit: stockPageSize, search: stockSearch || undefined });
    }
  }, [tab, id, stockPage, stockPageSize]);

  React.useEffect(() => {
    if (tab === 2) {
      getBazaarPurchases(id, {
        page: purchasePage,
        limit: purchasePageSize,
        search: purchaseSearch || undefined,
        paymentMethod: purchasePayment || undefined,
      });
    }
  }, [tab, id, purchasePage, purchasePageSize, purchasePayment]);

  const handleStockSearch = () => {
    setStockPage(1);
    getBazaarStock(id, { page: 1, limit: stockPageSize, search: stockSearch || undefined });
  };

  const handlePurchaseSearch = () => {
    setPurchasePage(1);
    getBazaarPurchases(id, {
      page: 1,
      limit: purchasePageSize,
      search: purchaseSearch || undefined,
      paymentMethod: purchasePayment || undefined,
    });
  };

  const canEdit = bazaar && ['Pending', 'Accepted'].includes(bazaar.status);
  const displayStatus = bazaar?.displayStatus || bazaar?.status;
  const perf = performance || {};
  const unitsSold = React.useMemo(() => (
    (purchasesPreview || []).reduce(
      (acc, p) => acc + (p.lineItems || []).reduce(
        (a, li) => a + li.quantity - (li.quantityReturned || 0),
        0
      ),
      0
    )
  ), [purchasesPreview]);

  const purchaseCount = React.useMemo(
    () => perf.paymentBreakdown?.reduce((a, p) => a + p.count, 0) || 0,
    [perf.paymentBreakdown]
  );

  const statsSummary = (
    <div
      className="cz-g2"
      style={{
        background: 'var(--card-solid)',
        borderRadius: 26,
        padding: isMobile ? '18px 20px' : '24px 34px',
        display: 'grid',
        gridTemplateColumns: isMobile ? '1fr 1fr' : 'repeat(3, 1fr)',
        gap: 24,
        alignItems: 'center',
        border: '1px solid var(--s12)',
        boxShadow: 'inset 0 1px 0 var(--s14), 0 22px 50px var(--sh45)',
      }}
    >
      {[
        { label: t('bazaars.totalRevenue'), value: `${formatEgp(perf.totalRevenue || 0)} EGP`, sub: t('bazaars.dashboardRevenue') },
        { label: t('bazaars.purchaseCount'), value: formatInt(purchaseCount), sub: t('bazaars.tabPurchases') },
        { label: t('bazaars.unitsSold'), value: formatInt(unitsSold), sub: t('bazaars.dashboardUnits') },
      ].map((st) => (
        <div key={st.label}>
          <div style={{ fontSize: 12, color: 'var(--mut2)', marginBottom: 10 }}>{st.label}</div>
          <div style={{ fontSize: isMobile ? 20 : 26, fontWeight: 500, color: 'var(--tx)' }}>
            {isLoading ? <Skeleton width={64} /> : st.value}
          </div>
          <div style={{ fontSize: 11, color: 'var(--mut2)', marginTop: 6 }}>{st.sub}</div>
        </div>
      ))}
    </div>
  );

  const overviewTab = (
    <Grid container spacing={2}>
      <Grid item xs={12} md={6}>
        <Typography><strong>{t('bazaars.location')}:</strong> {bazaar?.location}</Typography>
        <Typography><strong>{t('bazaars.dates')}:</strong> {moment(bazaar?.startDate).format('LL')} – {moment(bazaar?.endDate).format('LL')}</Typography>
        <Typography><strong>{t('bazaars.times')}:</strong> {bazaar?.startTime} – {bazaar?.endTime}</Typography>
        <Typography><strong>{t('bazaars.mode')}:</strong> {bazaar?.fulfillmentMode === 'POS' ? t('bazaars.modePOS') : t('bazaars.modeOutbound')}</Typography>
        {bazaar?.status === 'Rejected' && bazaar?.rejectionReason && (
          <Typography sx={{ mt: 1 }}><strong>{t('bazaars.rejectionReason')}:</strong> {bazaar.rejectionReason}</Typography>
        )}
      </Grid>
      <Grid item xs={12} md={6}>
        {bazaar?.kycQuestions?.length > 0 && (
          <>
            <Typography variant="subtitle1" fontWeight="bold">{t('bazaars.kycQuestions')}</Typography>
            <ul style={{ margin: 0, paddingInlineStart: 20 }}>
              {bazaar.kycQuestions.map((q, i) => (
                <li key={i}>{q.question}{q.required ? ` (${t('bazaars.required')})` : ''}</li>
              ))}
            </ul>
          </>
        )}
      </Grid>
      <Grid item xs={12}>
        <Typography variant="h6">{t('bazaars.requestedItems')}</Typography>
        <Table
          size="small"
          pagination={false}
          locale={{ emptyText: <Empty description={t('bazaars.empty')} /> }}
          dataSource={bazaar?.lineItems?.map((li, i) => ({
            key: i,
            sku: li.product?.sku || li.product?.externalId,
            name: li.product?.name,
            qty: li.quantityRequested,
          }))}
          columns={[
            { title: t('common.sku'), dataIndex: 'sku' },
            { title: t('common.name'), dataIndex: 'name' },
            { title: t('common.qty'), dataIndex: 'qty' },
          ]}
        />
      </Grid>
      {bazaar?.fulfilledItems?.length > 0 && (
        <Grid item xs={12}>
          <Typography variant="h6">{t('bazaars.sentItems')}</Typography>
          <Table
            size="small"
            pagination={false}
            dataSource={bazaar.fulfilledItems.map((fi, i) => ({
              key: i,
              sku: fi.product?.sku || fi.product?.externalId,
              name: fi.product?.name,
              requested: fi.quantityRequested,
              sent: fi.quantitySent,
              variance: fi.quantitySent - fi.quantityRequested,
            }))}
            columns={[
              { title: t('common.sku'), dataIndex: 'sku' },
              { title: t('common.name'), dataIndex: 'name' },
              { title: t('bazaars.requested'), dataIndex: 'requested' },
              { title: t('bazaars.sent'), dataIndex: 'sent' },
              {
                title: t('bazaars.variance'),
                render: (_, row) => (
                  <Tag color={row.variance === 0 ? 'green' : 'orange'}>
                    {row.variance > 0 ? `+${row.variance}` : row.variance}
                  </Tag>
                ),
              },
            ]}
          />
        </Grid>
      )}
      {shipments?.length > 0 && (
        <Grid item xs={12}>
          <Typography variant="h6">{t('bazaars.shipments')}</Typography>
          {shipments.map((sh, idx) => (
            <Box key={sh._id || idx} sx={{ mb: 2, p: 2, border: '1px solid #eee', borderRadius: 1 }}>
              <Typography variant="body2" color="text.secondary">
                {moment(sh.createdAt).format('LLL')}
              </Typography>
              <Table
                size="small"
                pagination={false}
                dataSource={sh.items?.map((item, i) => ({
                  key: i,
                  sku: item.product?.sku || item.product?.externalId,
                  name: item.product?.name,
                  sent: item.quantitySent,
                }))}
                columns={[
                  { title: t('common.sku'), dataIndex: 'sku' },
                  { title: t('common.name'), dataIndex: 'name' },
                  { title: t('bazaars.sent'), dataIndex: 'sent' },
                ]}
              />
            </Box>
          ))}
        </Grid>
      )}
    </Grid>
  );

  const stockTab = (
    <>
      <Button sx={{ ...main_button, mb: 2 }} onClick={() => setShowStockFilters(!showStockFilters)}>
        {showStockFilters ? <FilterListOff /> : <FilterList />} {t('common.filter')}
      </Button>
      <Collapse in={showStockFilters}>
        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid item xs={12} md={8}>
            <MainInput
              fullWidth
              placeholder={t('common.search')}
              value={stockSearch}
              onChange={({ target }) => setStockSearch(target.value)}
              onKeyPress={(e) => { if (e.key === 'Enter') handleStockSearch(); }}
              InputProps={{ endAdornment: <Search sx={{ cursor: 'pointer' }} onClick={handleStockSearch} /> }}
            />
          </Grid>
        </Grid>
      </Collapse>
      <Table
        loading={isLoading}
        locale={{ emptyText: <Empty description={t('bazaars.emptyStock')} /> }}
        dataSource={(bazaarStock?.docs || []).map((s, i) => ({
          key: s._id || i,
          sku: s.product?.sku || s.product?.externalId,
          name: s.product?.name,
          amount: s.amount,
        }))}
        columns={[
          { title: t('common.sku'), dataIndex: 'sku' },
          { title: t('common.name'), dataIndex: 'name' },
          { title: t('bazaars.onHand'), dataIndex: 'amount' },
        ]}
        onChange={(pagination) => {
          setStockPage(pagination.current);
          setStockPageSize(pagination.pageSize);
        }}
        pagination={{
          current: stockPage,
          pageSize: stockPageSize,
          total: bazaarStock?.totalDocs || 0,
          showSizeChanger: true,
        }}
      />
    </>
  );

  const purchasesTab = (
    <>
      <Button sx={{ ...main_button, mb: 2 }} onClick={() => setShowPurchaseFilters(!showPurchaseFilters)}>
        {showPurchaseFilters ? <FilterListOff /> : <FilterList />} {t('common.filter')}
      </Button>
      <Collapse in={showPurchaseFilters}>
        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid item xs={12} md={6}>
            <MainInput
              fullWidth
              label={t('bazaars.searchReceipt')}
              placeholder="#POS-BRAND-1001"
              value={purchaseSearch}
              onChange={({ target }) => setPurchaseSearch(target.value)}
              onKeyPress={(e) => { if (e.key === 'Enter') handlePurchaseSearch(); }}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <FormControl fullWidth>
              <InputLabel>{t('bazaars.payment')}</InputLabel>
              <Select value={purchasePayment} label={t('bazaars.payment')}
                onChange={(e) => { setPurchasePayment(e.target.value); setPurchasePage(1); }}>
                <MenuItem value="">{t('bazaars.allStatuses')}</MenuItem>
                {PAYMENT_METHODS.map((m) => (
                  <MenuItem key={m} value={m}>{paymentLabel(m)}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={2}>
            <Button sx={sec_button} fullWidth onClick={handlePurchaseSearch}>{t('common.search')}</Button>
          </Grid>
        </Grid>
      </Collapse>
      <Table
        loading={isLoading}
        locale={{ emptyText: <Empty description={t('bazaars.emptyPurchases')} /> }}
        dataSource={(bazaarPurchases?.docs || []).map((p) => ({ ...p, key: p._id }))}
        columns={[
          { title: t('bazaars.purchaseNumber'), dataIndex: 'purchaseNumber' },
          {
            title: t('common.status'),
            dataIndex: 'status',
            render: (val) => val || 'Active',
          },
          {
            title: t('bazaars.payment'),
            render: (_, r) => paymentLabel(r.paymentMethod),
          },
          { title: t('bazaars.total'), render: (_, r) => `${r.total?.toLocaleString()} EGP` },
          { title: t('common.date'), render: (_, r) => moment(r.createdAt).format('LLL') },
          {
            title: t('common.actions'),
            render: (_, r) => (
              <Button size="small" onClick={() => setPurchaseModal(r)}>{t('bazaars.viewPurchase')}</Button>
            ),
          },
        ]}
        onChange={(pagination) => {
          setPurchasePage(pagination.current);
          setPurchasePageSize(pagination.pageSize);
        }}
        pagination={{
          current: purchasePage,
          pageSize: purchasePageSize,
          total: bazaarPurchases?.totalDocs || 0,
          showSizeChanger: true,
        }}
      />
    </>
  );

  const performanceTab = (
    <Grid container spacing={2}>
      {perf.paymentBreakdown?.length > 0 && (
        <Grid item xs={12}>
          <Typography variant="h6">{t('bazaars.paymentBreakdown')}</Typography>
          <Table
            size="small"
            pagination={false}
            dataSource={perf.paymentBreakdown.map((p, i) => ({
              key: i,
              method: paymentLabel(p._id),
              count: p.count,
              total: p.total,
            }))}
            columns={[
              { title: t('bazaars.payment'), dataIndex: 'method' },
              { title: t('bazaars.purchaseCount'), dataIndex: 'count' },
              { title: t('bazaars.total'), render: (_, r) => `${r.total?.toLocaleString()} EGP` },
            ]}
          />
        </Grid>
      )}
      <Grid item xs={12} md={6}>
        <Typography variant="h6">{t('bazaars.topPerformers')}</Typography>
        <Table size="small" pagination={false}
          locale={{ emptyText: <Empty description={t('bazaars.emptyPurchases')} /> }}
          dataSource={(perf.top || []).map((p, i) => ({
            key: i, name: p.product?.name, sku: p.product?.sku || p.product?.externalId,
            units: p.unitsSold, revenue: p.revenue,
          }))}
          columns={[
            { title: t('common.sku'), dataIndex: 'sku' },
            { title: t('common.name'), dataIndex: 'name' },
            { title: t('bazaars.unitsSold'), dataIndex: 'units' },
            { title: t('bazaars.revenue'), render: (_, r) => `${r.revenue?.toLocaleString()} EGP` },
          ]}
        />
      </Grid>
      <Grid item xs={12} md={6}>
        <Typography variant="h6">{t('bazaars.worstPerformers')}</Typography>
        <Table size="small" pagination={false}
          locale={{ emptyText: <Empty description={t('bazaars.emptyPurchases')} /> }}
          dataSource={(perf.bottom || []).map((p, i) => ({
            key: i, name: p.product?.name, sku: p.product?.sku || p.product?.externalId,
            units: p.unitsSold, revenue: p.revenue,
          }))}
          columns={[
            { title: t('common.sku'), dataIndex: 'sku' },
            { title: t('common.name'), dataIndex: 'name' },
            { title: t('bazaars.unitsSold'), dataIndex: 'units' },
            { title: t('bazaars.revenue'), render: (_, r) => `${r.revenue?.toLocaleString()} EGP` },
          ]}
        />
      </Grid>
    </Grid>
  );

  if (!bazaar && isLoading) {
    return (
      <Box sx={{ ...centered_flex_box, minHeight: '50vh' }}>
        <CircularProgress sx={{ color: 'var(--secColor4)' }} />
      </Box>
    );
  }

  const detailTabs = [
    t('bazaars.tabOverview'),
    t('bazaars.tabStock'),
    t('bazaars.tabPurchases'),
    t('bazaars.tabPerformance'),
  ];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        <Hoverable
          as="button"
          type="button"
          className="cz-chip"
          hover={{}}
          onClick={() => navigate('/bazaars')}
        >
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M19 12H5" />
            <path d="m12 19-7-7 7-7" />
          </svg>
          {t('bazaars.title')}
        </Hoverable>
        <div style={{ ...pageTitle, fontSize: isMobile ? 22 : 28 }}>{bazaar?.name}</div>
        {displayStatus && <StatusPill status={displayStatus}>{statusLabel(displayStatus)}</StatusPill>}
        <div style={{ marginInlineStart: 'auto', display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <button type="button" className="cz-chip" onClick={loadOverview} disabled={isLoading}>
            {t('common.refresh')}
          </button>
          {canEdit && (
            <button type="button" className="cz-chip red" onClick={() => navigate(`/bazaars/${id}/edit`)}>
              {t('common.edit')}
            </button>
          )}
        </div>
      </div>

      {bazaar?.fulfillmentMode === 'POS' && statsSummary}

      <div className="cz-tabs" style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        {detailTabs.map((label, idx) => (
          <button
            key={label}
            type="button"
            className={`cz-tab ${tab === idx ? 'on' : ''}`}
            onClick={() => setTab(idx)}
          >
            {label}
          </button>
        ))}
      </div>

      <div style={cardShell}>
        {tab === 0 && overviewTab}
        {tab === 1 && stockTab}
        {tab === 2 && purchasesTab}
        {tab === 3 && performanceTab}
      </div>

      <Dialog open={Boolean(purchaseModal)} onClose={() => setPurchaseModal(null)} maxWidth="md" fullWidth>
        <DialogTitle>{t('bazaars.purchaseDetail')} — {purchaseModal?.purchaseNumber}</DialogTitle>
        <DialogContent dividers>
          {purchaseModal && (
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Typography><strong>{t('common.date')}:</strong> {moment(purchaseModal.createdAt).format('LLL')}</Typography>
                <Typography><strong>{t('bazaars.payment')}:</strong> {paymentLabel(purchaseModal.paymentMethod)}</Typography>
                {(purchaseModal.refundTotal || 0) > 0 && (
                  <>
                    <Typography><strong>{t('bazaars.originalTotal')}:</strong> {(purchaseModal.originalTotal ?? purchaseModal.lineItems?.reduce((a, li) => a + li.lineTotal, 0))?.toLocaleString()} EGP</Typography>
                    <Typography><strong>{t('bazaars.refunded')}:</strong> −{purchaseModal.refundTotal?.toLocaleString()} EGP</Typography>
                  </>
                )}
                <Typography><strong>{t('bazaars.total')}:</strong> {purchaseModal.total?.toLocaleString()} EGP</Typography>
                <Typography><strong>{t('common.status')}:</strong> {purchaseModal.status || 'Active'}</Typography>
                {purchaseModal.customerName && (
                  <Typography><strong>{t('bazaars.customerName')}:</strong> {purchaseModal.customerName}</Typography>
                )}
                {purchaseModal.buyerEmail && (
                  <Typography><strong>{t('bazaars.buyerEmail')}:</strong> {purchaseModal.buyerEmail}</Typography>
                )}
                {purchaseModal.visaReceiptNumber && (
                  <Typography><strong>{t('bazaars.visaReceipt')}:</strong> {purchaseModal.visaReceiptNumber}</Typography>
                )}
                {purchaseModal.instapayPhone && (
                  <Typography><strong>{t('bazaars.instapayPhone')}:</strong> {purchaseModal.instapayPhone}</Typography>
                )}
                {purchaseModal.cashier?.name && (
                  <Typography><strong>{t('bazaars.cashier')}:</strong> {purchaseModal.cashier.name}</Typography>
                )}
              </Grid>
              <Grid item xs={12}>
                <Table
                  size="small"
                  pagination={false}
                  dataSource={purchaseModal.lineItems?.map((li, i) => ({
                    key: i,
                    sku: li.product?.sku || li.product?.externalId,
                    name: li.product?.name,
                    bought: li.quantity,
                    returned: li.quantityReturned || 0,
                    kept: li.quantity - (li.quantityReturned || 0),
                    unitPrice: li.unitPrice,
                    lineTotal: (li.quantity - (li.quantityReturned || 0)) * li.unitPrice,
                  }))}
                  columns={[
                    { title: t('common.sku'), dataIndex: 'sku' },
                    { title: t('common.name'), dataIndex: 'name' },
                    { title: t('bazaars.bought'), dataIndex: 'bought' },
                    { title: t('bazaars.returned'), dataIndex: 'returned' },
                    { title: t('bazaars.kept'), dataIndex: 'kept' },
                    { title: t('bazaars.unitPrice'), render: (_, r) => `${r.unitPrice?.toLocaleString()} EGP` },
                    { title: t('bazaars.total'), render: (_, r) => `${r.lineTotal?.toLocaleString()} EGP` },
                  ]}
                />
              </Grid>
              {purchaseModal.kycAnswers?.length > 0 && (
                <Grid item xs={12}>
                  <Typography variant="subtitle1" fontWeight="bold">{t('bazaars.kycAnswers')}</Typography>
                  {purchaseModal.kycAnswers.map((a, i) => (
                    <Typography key={i}><strong>{a.question}:</strong> {a.answer}</Typography>
                  ))}
                </Grid>
              )}
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPurchaseModal(null)}>{t('common.close')}</Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

const mapStateToProps = (state) => ({
  bazaar: state.records.bazaar,
  shipments: state.records.shipments,
  performance: state.records.performance,
  purchasesPreview: state.records.purchases,
  bazaarStock: state.records.bazaarStock,
  bazaarPurchases: state.records.bazaarPurchases,
  isLoading: state.records.isLoading,
});

export default connect(mapStateToProps, { getBazaar, getBazaarStock, getBazaarPurchases })(BazaarDetail);
